/**
 * StatusDot
 * Minimal status indicator - just a colored dot with optional pulse animation.
 */

import { cn } from '@/lib/utils';

import type { RenderStatus, TransmissionStatus } from '../../types';

interface StatusDotProps {
  renderStatus: RenderStatus;
  transmissionStatus?: TransmissionStatus | null;
  className?: string;
}

export const StatusDot: React.FC<StatusDotProps> = ({
  renderStatus,
  transmissionStatus,
  className,
}) => {
  // Transmission status takes priority
  if (transmissionStatus) {
    const config = TX_DOT_CONFIG[transmissionStatus];
    return (
      <div
        className="flex items-center justify-center h-4 w-4"
        title={config.label}
      >
        <span
          className={cn(
            'h-2 w-2 rounded-full transition-all duration-300',
            config.color,
            config.animate &&
              'animate-pulse shadow-[0_0_0_2px_rgba(255,255,255,0.5)]',
            className,
          )}
        />
      </div>
    );
  }

  const config = RENDER_DOT_CONFIG[renderStatus];
  return (
    <div
      className="flex items-center justify-center h-4 w-4"
      title={config.label}
    >
      <span
        className={cn(
          'h-2 w-2 rounded-full transition-all duration-300',
          config.color,
          config.animate &&
            'animate-pulse shadow-[0_0_0_2px_rgba(255,255,255,0.5)]',
          className,
        )}
      />
    </div>
  );
};

interface DotConfig {
  color: string;
  label: string;
  animate?: boolean;
}

const RENDER_DOT_CONFIG: Record<RenderStatus, DotConfig> = {
  draft: { color: 'bg-stone-300', label: 'Draft' },
  queued_for_render: {
    color: 'bg-violet-500',
    label: 'Queued',
    animate: true,
  },
  generating: { color: 'bg-blue-500', label: 'Generating', animate: true },
  ready: { color: 'bg-emerald-500', label: 'Ready to send' },
  published: { color: 'bg-indigo-500', label: 'Published' },
  missing_fields: { color: 'bg-amber-500', label: 'Missing fields' },
  error: { color: 'bg-red-500', label: 'Error' },
};

const TX_DOT_CONFIG: Record<TransmissionStatus, DotConfig> = {
  pending: { color: 'bg-yellow-500', label: 'Queued', animate: true },
  sending: { color: 'bg-blue-500', label: 'Sending', animate: true },
  sent: { color: 'bg-indigo-500', label: 'Sent' },
  delivered: { color: 'bg-emerald-500', label: 'Delivered' },
  failed: { color: 'bg-red-500', label: 'Failed' },
  bounced: { color: 'bg-red-500', label: 'Bounced' },
};
